import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getLeagueById, getLeagueTable } from "../services/leagueService";
import { getErrorMessage } from "../services/api";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import EmptyState from "../components/EmptyState";
import StatPill from "../components/StatPill";
import AlertMessage from "../components/AlertMessage";
import BackButton from "../components/BackButton";
import LeagueTableRow from "../components/LeagueTableRow";

function LeagueTablePage({ userId }) {
  const { leagueId } = useParams();
  const numericLeagueId = Number(leagueId);

  const [league, setLeague] = useState(null);
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (!Number.isInteger(numericLeagueId) || numericLeagueId <= 0) {
      setLoading(false);
      setMessage("Invalid league.");
      return;
    }

    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setMessage("");

      try {
        const [leagueData, tableData] = await Promise.all([
          getLeagueById(numericLeagueId, { signal: controller.signal }),
          getLeagueTable(numericLeagueId, { signal: controller.signal }),
        ]);

        setLeague(leagueData || null);
        setTable(tableData || null);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error(error);
        setMessage(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    }

    load();

    return () => controller.abort();
  }, [userId, numericLeagueId]);

  if (!userId) {
    return (
      <SectionCard>
        <p className="text-slate-600">Loading user...</p>
      </SectionCard>
    );
  }

  if (!Number.isInteger(numericLeagueId) || numericLeagueId <= 0) {
    return (
      <SectionCard>
        <p className="text-slate-600">Invalid league.</p>
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-9 w-52 animate-pulse rounded-full bg-slate-200" />
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 p-6 sm:p-8">
          <div className="h-3 w-16 animate-pulse rounded bg-slate-700" />
          <div className="mt-3 h-8 w-48 animate-pulse rounded bg-slate-700" />
          <div className="mt-2 h-4 w-40 animate-pulse rounded bg-slate-700" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl bg-slate-50 px-4 py-4 ring-1 ring-slate-200">
              <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
              <div className="mt-2 h-6 w-24 animate-pulse rounded bg-slate-200" />
            </div>
          ))}
        </div>
        <SectionCard title="Standings">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        </SectionCard>
      </div>
    );
  }

  const rows = Array.isArray(table?.rows) ? table.rows : [];

  const leader =
    rows.length > 0
      ? [...rows].sort((a, b) => b.totalPoints - a.totalPoints)[0]
      : null;

  return (
    <div className="space-y-8">
      <BackButton to={`/leagues/${numericLeagueId}`} label="Back to League Overview" />

      <PageHeader
        eyebrow="League"
        title={`${league?.name || table?.leagueName || "League"} Table`}
        subtitle="Track rankings and compare performance."
        dark
        rightContent={
          <div className="rounded-2xl bg-white/10 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-white">
            Invite code:{" "}
            <span className="font-semibold break-all">{league?.inviteCode || "—"}</span>
          </div>
        }
      />

      <AlertMessage message={message} type="error" />

      <section className="grid gap-4 sm:grid-cols-3">
        <StatPill label="Members" value={league?.memberCount ?? rows.length} />
        <StatPill label="Leader" value={leader?.displayName || "—"} />
        <StatPill label="Top Score" value={leader ? `${leader.totalPoints} pts` : "—"} />
      </section>

      {rows.length === 0 ? (
        <EmptyState
          title="No table data yet"
          description="Standings will appear once points are available."
        />
      ) : (
        <SectionCard title="Standings">
          <div className="space-y-3">
            <div className="hidden md:grid grid-cols-[56px_1fr_88px_88px_110px] gap-4 px-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              <div>Pos</div>
              <div>Player</div>
              <div className="text-right">Points</div>
              <div className="text-right">Exact</div>
              <div className="text-right">Correct</div>
            </div>

            {rows.map((row) => (
              <LeagueTableRow key={row.userId} row={row} />
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

export default LeagueTablePage;
